// eslint-disable-next-line no-use-before-define
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Drawer } from '../../drawer';
import { ChatDrawerContainer } from './style';
import { useTransport } from '../../../hook/use-transport';
/**
 * Eventos que a RootApp emite e que o chat também recebe.
 *
 * A lista é fechada de propósito. `mfe.unmount` sai pelo mesmo canal e é
 * dirigido ao MFE do palco — entregá-lo aqui derrubaria o chat a cada
 * navegação. `assistance.filtersCleared` é sobre filtros de tela, que o chat
 * não tem.
 */
const CHAT_EVENTS = [
    // idioma e tema seguem a preferência do usuário enquanto o drawer está aberto
    'mfe.on.lang',
    'mfe.on.theme',
    // o chat acompanha em que tela o usuário está
    'router.on.navigate',
    // a página vai recarregar: deixa o chat encerrar o que estiver em voo
    'assistance.packageUpdating',
];
const DRAWER_STYLE_ID = 'root-app-chat-drawer-overrides';
/**
 * Ajustes de layout aplicados ao chat quando ele roda dentro do drawer.
 *
 * O seletor é por substring de propósito. O chat usa CSS Modules, e o nome real
 * da classe carrega um hash de build (`style-module__chatScreen__Pfqkw`) que
 * muda a cada deploy dele — casar o nome exato quebraria sem aviso na próxima
 * publicação.
 *
 * O `!important` é o que garante a precedência: o chat injeta o próprio CSS em
 * tempo de execução, então a ordem das folhas não é confiável. A regra dele não
 * é important, então esta vence independentemente de quem entrou primeiro.
 */
const DRAWER_OVERRIDES = `
  [class*='chatScreen'] {
    padding: 0 !important;
  }

  [class*='ChatContainer-module__footer'] {
    margin-top: 16px !important;
    margin-bottom: 16px !important;
  }

  [class*='MessageInput-module__sendButton'] {
    margin-top: 16px !important;
  }
`;
/**
 * Injeta o CSS acima **dentro** do documento do iframe.
 *
 * Estilo do documento pai não atravessa a fronteira do iframe, então não
 * adianta declarar essa regra no styled-component do drawer. Como o chat é
 * servido da mesma origem da RootApp, dá para alcançar o `document` dele.
 *
 * É idempotente: o `onLoad` dispara de novo se o iframe recarregar, e o id
 * evita empilhar folhas repetidas.
 */
const applyDrawerStyles = (doc) => {
    if (doc == null || doc.getElementById(DRAWER_STYLE_ID) != null) {
        return;
    }
    try {
        const style = doc.createElement('style');
        style.id = DRAWER_STYLE_ID;
        style.textContent = DRAWER_OVERRIDES;
        doc.head.appendChild(style);
    }
    catch {
        // Se o documento do chat ficar inacessível, o layout dele apenas mantém o
        // padding — não vale derrubar a montagem do drawer por causa disso.
    }
};
/**
 * Botao de enviar do chat, cuja largura o "Fechar" do rodape espelha.
 *
 * Casa por substring pelo mesmo motivo dos seletores acima, mas incluindo o
 * nome do modulo: so `sendButton` seria generico demais, e `footer` — usado
 * nos overrides — casaria com qualquer rodape do chat. O que muda entre builds
 * e apenas o hash do fim (`MessageInput-module__sendButton__w2Rwt`).
 */
const SEND_BUTTON_SELECTOR = "[class*='MessageInput-module__sendButton']";
/**
 * Variavel lida pelo rodape em `drawer/style.ts`. E definida no painel deste
 * drawer, e nao no documento, para nao vazar para o "Cancelar" dos outros.
 */
const CLOSE_WIDTH_VAR = '--root-app-chat-close-width';
const DRAWER_PANEL_SELECTOR = '.root-app_drawer';
export const ChatDrawerComponent = ({ isOpen, onClose, chatMfeUrl, chatMfeReady, registerChatWindow, unregisterChatWindow, }) => {
    const iframeRef = useRef(null);
    const observersRef = useRef([]);
    const { transport } = useTransport();
    /**
     * O iframe só nasce na primeira abertura — não vale pagar o carregamento do
     * MFE para quem nunca abre o chat. Depois disso ele permanece montado: o
     * drawer fechado é `display: none` (ver `components/drawer/style.ts`), então
     * a conversa sobrevive ao fecha-e-abre em vez de recarregar do zero.
     */
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        if (isOpen) {
            setMounted(true);
        }
    }, [isOpen]);
    /**
     * Enquanto este iframe existir, ele recebe os eventos de `CHAT_EVENTS`.
     *
     * A inscrição é desfeita ao desmontar: o `Transport` guarda a referência da
     * janela, e uma janela morta na lista só geraria trabalho a cada evento.
     */
    useEffect(() => () => {
        const contentWindow = iframeRef.current?.contentWindow;
        observersRef.current.forEach((observer) => observer.disconnect());
        observersRef.current = [];
        if (contentWindow != null) {
            transport?.removeEventTarget(contentWindow);
            unregisterChatWindow?.(contentWindow);
        }
    }, [transport, unregisterChatWindow]);
    /**
     * Espelha no "Fechar" a largura do botao de enviar do chat.
     *
     * A largura vem de dentro do iframe, entao nao ha como expressa-la em CSS: e
     * medida aqui e publicada como variavel CSS no painel deste drawer.
     *
     * Sao dois observadores porque sao dois momentos distintos. O botao so existe
     * depois que o chat monta, bem depois do onLoad — dai o MutationObserver. E a
     * largura dele muda com o texto e o layout — dai o ResizeObserver.
     *
     * Os observadores sao os do documento pai, aplicados a elementos do iframe:
     * mesma origem, entao funciona, e evita depender da tipagem da Window de la.
     *
     * Se o chat renomear a classe, o seletor deixa de casar, a variavel nunca e
     * definida e o botao volta a largura automatica. Degrada, nao quebra.
     */
    const syncCloseButtonWidth = (doc) => {
        const panel = iframeRef.current?.closest(DRAWER_PANEL_SELECTOR);
        const sendButton = doc.querySelector(SEND_BUTTON_SELECTOR);
        if (!(panel instanceof HTMLElement) ||
            !(sendButton instanceof HTMLElement)) {
            return null;
        }
        panel.style.setProperty(CLOSE_WIDTH_VAR, `${sendButton.offsetWidth}px`);
        return sendButton;
    };
    const watchCloseButtonWidth = (doc) => {
        observersRef.current.forEach((observer) => observer.disconnect());
        observersRef.current = [];
        const resizeObserver = new ResizeObserver(() => syncCloseButtonWidth(doc));
        observersRef.current.push(resizeObserver);
        const sync = () => {
            const sendButton = syncCloseButtonWidth(doc);
            if (sendButton == null) {
                return;
            }
            resizeObserver.disconnect();
            resizeObserver.observe(sendButton);
        };
        const mutationObserver = new MutationObserver(sync);
        mutationObserver.observe(doc.body, { childList: true, subtree: true });
        observersRef.current.push(mutationObserver);
        sync();
    };
    /**
     * Duas ligações que o iframe do chat não ganha de graça, feitas assim que
     * ele carrega:
     *
     * 1. O `rFetch` (fetch com os headers de autenticação) só é injetado no
     *    iframe dos MFEs pelo `infrastruture/intercept-fetch.ts`, que enxerga
     *    apenas o `mfeStage`. Sem a ponte o chat chamaria a API sem
     *    `Authorization`.
     * 2. Os eventos empurrados pela RootApp vão para o `target` do transporte —
     *    também o `mfeStage`. O `addEventTarget` inscreve este iframe na lista
     *    fechada de `CHAT_EVENTS`.
     * 3. O MFE não se desenha sozinho: ele anuncia `mfe.initialized` e espera o
     *    ask `mfe.mount` de volta. A RootApp só sabe para onde mandar esse ask
     *    se a janela estiver registrada — sem isso, tela branca.
     * 4. O ajuste de layout do chat dentro do drawer (ver `applyDrawerStyles`).
     *
     * Perguntas (`askAndWaitForResponse`) já funcionam sem nada disso: o
     * `Transport` responde para a janela que perguntou.
     *
     * Mesma origem da RootApp, então mexer no `contentWindow` é permitido.
     */
    const connectIframe = () => {
        const contentWindow = iframeRef.current?.contentWindow;
        if (contentWindow == null) {
            return;
        }
        if (typeof rFetch === 'function') {
            contentWindow.fetch = rFetch;
        }
        transport?.addEventTarget(contentWindow, CHAT_EVENTS);
        // Antes do `onLoad` o MFE ainda não anunciou nada, então registrar aqui
        // chega a tempo do handshake.
        registerChatWindow?.(contentWindow);
        applyDrawerStyles(contentWindow.document);
        watchCloseButtonWidth(contentWindow.document);
    };
    /**
     * Esc fecha o drawer.
     *
     * No modo bare nao ha botao de fechar — sobraria so o clique no overlay,
     * que nao e obvio e nem alcancavel por teclado.
     */
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    const { t } = useTranslation('drawerChat');
    return ReactDOM.createPortal(React.createElement(Drawer, { hideHeader: true, compactFooter: true, isOpen: isOpen, onClose: onClose, title: t('drawerChat:title'), cancelButton: { text: t('drawerChat:btnClose') } },
        React.createElement(ChatDrawerContainer, null,
            mounted && chatMfeUrl != null && (React.createElement("iframe", { ref: iframeRef, src: chatMfeUrl, title: t('drawerChat:title'), onLoad: connectIframe })),
            mounted && chatMfeUrl != null && !chatMfeReady && (React.createElement("div", { className: "chat-drawer_skeleton", role: "status", "aria-live": "polite", "aria-label": t('drawerChat:loading') },
                React.createElement("div", { className: "chat-drawer_skeleton-messages" },
                    React.createElement("div", { className: "chat-drawer_skeleton-bubble" }),
                    React.createElement("div", { className: "chat-drawer_skeleton-bubble chat-drawer_skeleton-bubble--sent" }),
                    React.createElement("div", { className: "chat-drawer_skeleton-bubble" })),
                React.createElement("div", { className: "chat-drawer_skeleton-input" }))),
            mounted && chatMfeUrl == null && (React.createElement("p", { className: "chat-drawer_unavailable" }, t('drawerChat:unavailable'))))), document.getElementById('root-app_side_content'));
};
//# sourceMappingURL=component.js.map